import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n/I18nProvider";
import { toast } from "sonner";
import { Inbox, ShoppingCart, ThumbsUp, ThumbsDown, Clock, AlertCircle, PackagePlus, Check } from "lucide-react";

const TAG_CLASS = {
  pending: "tag",
  approved: "tag tag-moss",
  rejected: "tag tag-brick",
  completed: "tag tag-slate",
  cancelled: "tag",
  expired: "tag tag-brick",
};

function StatusTag({ status, t }) {
  return <span className={TAG_CLASS[status] || "tag"}>{t(`status.${status}`)}</span>;
}

function ExpiryBadge({ b, t }) {
  if (b.status !== "pending" || !b.expires_at) return null;
  const ms = new Date(b.expires_at).getTime() - Date.now();
  if (ms <= 0) return null;
  const totalH = ms / (1000 * 60 * 60);
  const days = Math.floor(totalH / 24);
  const hours = Math.floor(totalH - days * 24);
  const urgent = totalH < 48;
  const label =
    days >= 1
      ? t("dash.expiresInDays").replace("{n}", days)
      : t("dash.expiresInHours").replace("{n}", Math.max(1, hours));
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-1 border ${urgent ? "border-[var(--brick)] text-[var(--brick)]" : "border-[var(--ink-200)] text-[var(--ink-700)]"}`}
      title={t("dash.expiresHint")}
      data-testid={`expiry-${b.id}`}
    >
      {urgent ? <AlertCircle size={12} /> : <Clock size={12} />}
      {label}
    </span>
  );
}

export function SellerBookings({ bookings, onUpdateStatus }) {
  const { t } = useI18n();

  if (bookings.length === 0) {
    return (
      <div className="text-center py-16">
        <Inbox size={32} className="mx-auto text-[var(--ink-500)]" />
        <h3 className="font-display text-2xl mt-3">{t("dash.noIncoming")}</h3>
      </div>
    );
  }

  return (
    <div className="divide-y divide-[var(--ink-200)]">
      {bookings.map((b) => (
        <div key={b.id} className="py-4 flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[240px]">
            <div className="font-display text-lg">{b.listing_title}</div>
            <div className="text-sm text-[var(--ink-700)] mt-1">
              {t("dash.from")} {b.buyer_name} · {b.quantity} · {t(`detail.${b.method}`)}{b.address ? ` → ${b.address}` : ""}
            </div>
            {b.note && <div className="text-xs text-[var(--ink-500)] mt-1 italic">"{b.note}"</div>}
          </div>
          <StatusTag status={b.status} t={t} />
          <ExpiryBadge b={b} t={t} />
          {b.status === "pending" && (
            <div className="flex gap-2">
              <button onClick={() => onUpdateStatus(b.id, "approved")} className="btn-primary text-xs" data-testid={`approve-${b.id}`}>{t("dash.approve")}</button>
              <button onClick={() => onUpdateStatus(b.id, "rejected")} className="btn-ghost text-xs" data-testid={`reject-${b.id}`}>{t("dash.reject")}</button>
            </div>
          )}
          {b.status === "approved" && (
            <button onClick={() => onUpdateStatus(b.id, "completed")} className="btn-ghost text-xs" data-testid={`complete-${b.id}`}>{t("dash.complete")}</button>
          )}
        </div>
      ))}
    </div>
  );
}

function BuyerBookingRow({ b, t, onUpdateStatus, ratedMap, onRate, onTransferred }) {
  const rated = ratedMap?.[b.id];
  const showRating = b.status === "completed";
  const navigate = useNavigate();
  const alreadyTransferred = !!b.transferred_listing_id;
  // Local "dismissed" state so a "No"-click hides the prompt for this session.
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const showTransferPrompt = b.status === "completed" && !alreadyTransferred && !dismissed;

  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${b.id}/transfer`);
      toast.success(t("transfer.success"));
      onTransferred && onTransferred(b.id, data.listing);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="py-4">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[240px]">
          <Link to={`/listings/${b.listing_id}`} className="font-display text-lg hover:underline">{b.listing_title}</Link>
          <div className="text-sm text-[var(--ink-700)] mt-1">
            {b.quantity} · {t(`detail.${b.method}`)}{b.address ? ` → ${b.address}` : ""}
          </div>
        </div>
        <StatusTag status={b.status} t={t} />
        <ExpiryBadge b={b} t={t} />
        {b.status === "pending" && (
          <button onClick={() => onUpdateStatus(b.id, "cancelled")} className="btn-ghost text-xs" data-testid={`cancel-${b.id}`}>{t("dash.cancel")}</button>
        )}
        {showRating && !rated && (
          <div className="flex gap-2" data-testid={`rate-${b.id}`}>
            <button onClick={() => onRate(b, true)} className="btn-ghost text-xs" aria-label={t("rating.thumbUp")} data-testid={`thumb-up-${b.id}`}>
              <ThumbsUp size={14} />
            </button>
            <button onClick={() => onRate(b, false)} className="btn-ghost text-xs" aria-label={t("rating.thumbDown")} data-testid={`thumb-down-${b.id}`}>
              <ThumbsDown size={14} />
            </button>
          </div>
        )}
        {showRating && rated && (
          <span className="tag tag-moss inline-flex items-center gap-1">
            {rated.thumbs_up ? <ThumbsUp size={12} /> : <ThumbsDown size={12} />} {t("rating.rated")}
          </span>
        )}
        {alreadyTransferred && (
          <Link
            to={`/listings/${b.transferred_listing_id}`}
            className="tag tag-moss inline-flex items-center gap-1"
            data-testid={`transferred-tag-${b.id}`}
          >
            <Check size={12} /> {t("transfer.addedTag")}
          </Link>
        )}
      </div>

      {/* Transfer prompt — completed booking, not yet claimed */}
      {showTransferPrompt && (
        <div
          className="mt-3 p-3 border-2 flex flex-wrap items-center gap-3"
          style={{ borderColor: "var(--moss)", background: "color-mix(in srgb, var(--moss) 10%, var(--surface))" }}
          data-testid={`transfer-prompt-${b.id}`}
        >
          <span
            className="inline-flex items-center justify-center w-9 h-9 shrink-0"
            style={{ background: "var(--moss)", color: "var(--bone)" }}
          >
            <PackagePlus size={16} />
          </span>
          <div className="flex-1 min-w-[200px]">
            <div className="text-sm font-semibold">{t("transfer.title")}</div>
            <div className="text-xs text-[var(--ink-500)] mt-0.5">{t("transfer.question")}</div>
          </div>
          <button
            onClick={claim}
            disabled={busy}
            className="btn-primary text-xs"
            data-testid={`transfer-yes-${b.id}`}
          >
            {busy ? "…" : t("transfer.yes")}
          </button>
          <button
            onClick={() => setDismissed(true)}
            disabled={busy}
            className="btn-ghost text-xs"
            data-testid={`transfer-no-${b.id}`}
          >
            {t("transfer.no")}
          </button>
        </div>
      )}
    </div>
  );
}

export function BuyerBookings({ bookings, onUpdateStatus }) {
  const { t } = useI18n();
  const [ratedMap, setRatedMap] = useState({});
  const [transferredMap, setTransferredMap] = useState({});

  useEffect(() => {
    api.get("/ratings/mine").then((r) => {
      const map = {};
      r.data.forEach((rt) => { map[rt.booking_id] = rt; });
      setRatedMap(map);
    }).catch(() => {});
  }, []);

  const onRate = async (booking, up) => {
    try {
      await api.post("/ratings", { booking_id: booking.id, thumbs_up: up });
      setRatedMap((m) => ({ ...m, [booking.id]: { booking_id: booking.id, thumbs_up: up } }));
      toast.success(t("rating.rated"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const onTransferred = (bookingId, newListing) => {
    setTransferredMap((m) => ({ ...m, [bookingId]: newListing.id }));
  };

  if (bookings.length === 0) {
    return (
      <div className="text-center py-16">
        <ShoppingCart size={32} className="mx-auto text-[var(--ink-500)]" />
        <h3 className="font-display text-2xl mt-3">{t("dash.noBookings")}</h3>
        <Link to="/marketplace" className="btn-primary mt-6 inline-flex" data-testid="empty-browse-btn">{t("dash.browse")}</Link>
      </div>
    );
  }

  return (
    <div className="divide-y divide-[var(--ink-200)]">
      {bookings.map((b) => {
        const merged = transferredMap[b.id] ? { ...b, transferred_listing_id: transferredMap[b.id] } : b;
        return (
          <BuyerBookingRow
            key={b.id}
            b={merged}
            t={t}
            onUpdateStatus={onUpdateStatus}
            ratedMap={ratedMap}
            onRate={onRate}
            onTransferred={onTransferred}
          />
        );
      })}
    </div>
  );
}
